import type AppRouter from '@omniwound/api';
import type { QueryClient, UseMutationResult } from '@tanstack/react-query';
import type { TRPCClient, TRPCClientErrorLike } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import {
  createTRPCContext,
  type TRPCOptionsProxy,
} from '@trpc/tanstack-react-query';
import type React from 'react';

const trpcContext = createTRPCContext<AppRouter>();

export const TRPCProvider: React.FC<{
  children: React.ReactNode;
  queryClient: QueryClient;
  trpcClient: TRPCClient<AppRouter>;
}> = trpcContext.TRPCProvider;

export const useTRPC: () => TRPCOptionsProxy<AppRouter> = trpcContext.useTRPC;

export const useTRPCClient: () => TRPCClient<AppRouter> =
  trpcContext.useTRPCClient;

export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * Procedure input types, keyed by procedure name. Used to type an authorized
 * mutation's payload without naming a shape the router already owns — several
 * procedure inputs are structural and have no exported alias in
 * `@omniwound/api`.
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

export type ApiError = TRPCClientErrorLike<AppRouter>;

/** The concrete error type produced by tRPC-backed TanStack mutations. */
export type ApiMutationResult<
  TData,
  TError extends Error,
  TVariables,
> = UseMutationResult<
  TData,
  ApiError & Omit<TError, keyof Error>,
  TVariables
>;
